import { useState } from 'react';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Box, FormHelperText, Typography } from '@screentone/core';

import { Autocomplete } from 'components/autocomplete';
import { brandIcon } from 'components/datamodel/content/Article/brandIcon';
import { useAllessehContentQuery } from 'hooks';
import { GLOBAL_PUBLICATION_KEY, usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContentQueryBody } from 'hooks/useAllessehContentQuery';
import { AuraEmployeeSearchResponse, useAuraQuery } from 'hooks/useAuraQuery';
import { useContentConstants } from 'hooks/useContentConstants';
import styles from './TextForm.module.scss';
import { getAllessehQueryTermsFromQueryRules, getQueryRulesFromAllessehQueryTerms } from '../../queryRulesFormUtils';

interface TextFormProps {
  allessehKey: string;
  value: string;
  onChange: (newValue: string) => void;
  // the current state of the full Allesseh query used by the Query form
  // (includes base publication settings query and the key + value for this TextForm)
  fullAllessehQuery: AllessehContentQueryBody;
  disabled: boolean;
  placeholder?: string;
  setOpen?: (o: boolean) => void;
  enableAura?: boolean;
}

const getAllessehQueryWithoutKey = (allessehKey: string, fullAllessehQuery: AllessehContentQueryBody) => {
  const queryRules = getQueryRulesFromAllessehQueryTerms(JSON.stringify(fullAllessehQuery));
  queryRules.all = queryRules.all.filter((r) => r.key !== allessehKey);
  // Drop all `any` (or) rules for aggregation requests — they narrow the result set but are
  // not required constraints, so keeping them causes aggregation to return empty values.
  queryRules.any = [];
  queryRules.not = queryRules.not.filter((r) => r.key !== allessehKey);
  const allessehQuery = getAllessehQueryTermsFromQueryRules(queryRules);
  return JSON.parse(allessehQuery) as AllessehContentQueryBody;
};

type AuraOption = { image: string | null; name: string };

export const TextForm = ({
  allessehKey,
  value,
  onChange,
  fullAllessehQuery,
  disabled,
  placeholder,
  setOpen,
  enableAura = false
}: TextFormProps) => {
  const { currentProperty } = useAuth();
  const { data: contentConstants } = useContentConstants();
  const { data: globalSettingsResp } = usePublicationSettings({ publicationKey: GLOBAL_PUBLICATION_KEY });
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const typed = value.trim();

  const Icon = brandIcon({ product: currentProperty, publicationMap: contentConstants?.publicationMap });

  const isAllesseh = !(enableAura && value.length > 0);

  const allessehQueryWithoutCurrentKey = isAllesseh
    ? getAllessehQueryWithoutKey(allessehKey, fullAllessehQuery)
    : undefined;

  const allesseh = useAllessehContentQuery(
    {
      ...allessehQueryWithoutCurrentKey,
      count: 1,
      aggregations: [allessehKey]
    },
    {
      enabled: isAllesseh && isAutocompleteOpen,
      publicationKey: globalSettingsResp?.publicationSetting.publicationKey
    }
  );

  const aura = useAuraQuery(typed, {
    mode: 'name',
    enabled: isAutocompleteOpen && typed.length > 0 && enableAura,
    currentProperty
  });

  const handleChange = (newValue: string) => {
    onChange(newValue);
  };

  let allessehOptions: string[] = [];
  if (isAllesseh) {
    const dataOptions = allesseh.data?.pages[0]?.meta?.aggregations[0]?.values ?? [];
    const mapped = dataOptions.map((v: { value: string }) => v.value);
    allessehOptions = mapped.filter((opt: string) => opt.toLowerCase().includes(value.toLowerCase()));
  }

  const auraOptions: AuraOption[] = [];
  if (aura.data) {
    (aura.data as AuraEmployeeSearchResponse).topics.forEach((topic) => {
      auraOptions.push({ image: topic.photo, name: topic.name });
    });
  }

  const isLoading = isAllesseh ? allesseh.isLoading : aura.isLoading;

  // Render header for author search
  const renderHeader =
    isAllesseh && enableAura
      ? () => (
          <Box padding={{ all: 'sm' }}>
            <Typography variant="header4" margin={{ bottom: 'none' }}>
              Most Published Authors
            </Typography>
            <FormHelperText>Type to search authors</FormHelperText>
          </Box>
        )
      : undefined;

  // Render option for Aura results with avatars
  const renderAuraOption = (option: AuraOption, onClickFromAutocomplete: () => void) => {
    const handleClick = () => {
      onClickFromAutocomplete();
      if (setOpen) setOpen(false);
    };
    return (
      <Box
        data-testid="text-form-option"
        padding={{ all: 'sm' }}
        className={isAllesseh ? styles.option : styles.optionAura}
        onClick={handleClick}
      >
        <div className={styles.avatarWrapper}>
          {option.image ? (
            <div className={styles.avatarContainer}>
              <img src={option.image} alt={option.name} className={styles.avatar} />
            </div>
          ) : (
            Icon && (
              <div className={styles.avatarContainer}>
                <span className={styles.avatar}>
                  <Icon size="lg" />
                </span>
              </div>
            )
          )}
          <Typography data-testid="text-form-option-label">{option.name}</Typography>
        </div>
      </Box>
    );
  };

  // Render option for Allesseh results
  const renderAllessehOption = (option: string, onClickFromAutocomplete: () => void) => {
    const handleClick = () => {
      onClickFromAutocomplete();
      if (setOpen) setOpen(false);
    };
    return (
      <Box data-testid="text-form-option" padding={{ all: 'sm' }} className={styles.option} onClick={handleClick}>
        <Typography data-testid="text-form-option-label">{option}</Typography>
      </Box>
    );
  };

  return (
    <div
      data-testid="text-form-container"
      className={styles.textForm}
      data-container={placeholder ? 'custom' : 'default'}
    >
      {isAllesseh ? (
        <Autocomplete<string>
          data-testid="text-form-input"
          value={value}
          onChange={handleChange}
          options={allessehOptions}
          disabled={disabled}
          placeholder={placeholder ?? 'Enter value or comma separated values'}
          isLoading={isLoading}
          renderHeader={renderHeader}
          renderOption={renderAllessehOption}
          onOpen={() => setIsAutocompleteOpen(true)}
          className="text-form-input"
        />
      ) : (
        <Autocomplete<AuraOption>
          data-testid="text-form-input"
          value={value}
          onChange={handleChange}
          options={auraOptions}
          disabled={disabled}
          placeholder={placeholder ?? 'Enter value or comma separated values'}
          isLoading={isLoading}
          getOptionKey={(option) => option.name.split(' ').join('')}
          getOptionValue={(option) => option.name}
          renderOption={renderAuraOption}
          onOpen={() => setIsAutocompleteOpen(true)}
          className="text-form-input"
        />
      )}
    </div>
  );
};
